import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { TITLE_PATH } from '../../actions/ScormActions';
import { SCORM_CONTENT_TYPE_OPTIONS } from '../../constants/ScormEnums';
import { useDidUpdateEffect, useFormSelector } from '../../custom-hooks';
import { isEmpty, isNilOrEmpty } from '../../utils/ramdaUtils';
import { applyDefaultValue, runInteractionRules } from '../actions/runtime.actions';
import { FORM_MODEL_ATTRIBUTES, SS_CONTROL_TYPE } from '../constants/form-manager.constants';
import { LOOKUP_TYPES } from '../constants/runtime.constants';
import { getUserMadeInteraction } from '../selectors/common.selectors';
import {
  getEvaluatedConditions,
  getEvaluatedExpression,
  getTranslatedLabel,
} from '../selectors/form-manager.selectors';
import { getDataByController } from '../selectors/runtime.selectors';
import { getRowIdFromEntityData } from '../selectors/ssGrid.selectors';
import { equalityFn } from '../utils/form-manager.utils';
import {
  bindControl,
  getAutoCompleteErrorsCache,
  releaseControl,
  setAutoCompleteErrorsCache,
} from '../utils/validationStorage.utils';

const TEMP_CONTROLS = {};

const targetParentField = (parentIndex, index) =>
  parentIndex.length > 1 && index.length < parentIndex.length;

const VirtualDynamicInput = ({
  id,
  index,
  entityName,
  fieldName,
  formKey,
  controlType,
  interaction,
  isViewOnly,
  isHidden: controlHidden,
  defaultLabel,
  containersProps,
  parentIndex,
  gridIndex,
  lookupType,
  scormContentTypeOptions = 'INITIAL',
}) => {
  const instanceId = `${id}#[${index}]`;
  const selectorProps = { id, index, entityName };

  const dispatch = useDispatch();
  const isRequired = useFormSelector(getEvaluatedConditions, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.REQUIRED,
  });
  const defaultValue = useFormSelector(
    getEvaluatedExpression,
    {
      ...selectorProps,
      prop: FORM_MODEL_ATTRIBUTES.DEFAULT_VALUE,
    },
    equalityFn
  );
  const evaluatedLabel = useFormSelector(getEvaluatedExpression, {
    ...selectorProps,
    prop: FORM_MODEL_ATTRIBUTES.LABEL,
  });
  const translatedLabel = useFormSelector(getTranslatedLabel, {
    entityName,
    fieldName,
    label: evaluatedLabel,
  });
  const userMadeInteraction = useFormSelector(getUserMadeInteraction);
  const value = useFormSelector(getDataByController, {
    entityName:
      controlType === SS_CONTROL_TYPE.SCORM_FILE_INPUT
        ? 'V_CUR_COURSE-V_DTI_DUT_TRAINING_INFO'
        : entityName,
    fieldName:
      controlType === SS_CONTROL_TYPE.SCORM_FILE_INPUT
        ? TITLE_PATH[SCORM_CONTENT_TYPE_OPTIONS[scormContentTypeOptions]]
        : fieldName,
    index,
  });
  const rowId = useFormSelector(getRowIdFromEntityData, selectorProps);
  const isDefaultValueApplied = useRef();

  const containersPropsJson = JSON.stringify(containersProps);
  const label = translatedLabel ?? defaultLabel;

  useEffect(() => {
    if (
      (!userMadeInteraction || !isEmpty(value)) &&
      !isDefaultValueApplied.current &&
      isNilOrEmpty(value) &&
      !_.isNil(defaultValue)
    ) {
      dispatch(applyDefaultValue({ value: defaultValue, id, index, formKey }));
      isDefaultValueApplied.current = true;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, instanceId]);

  useDidUpdateEffect(() => {
    if (interaction) {
      dispatch(runInteractionRules({ index, interaction, formKey }));
    }
  }, [value, instanceId]);

  useEffect(() => {
    if (lookupType !== LOOKUP_TYPES.AUTOCOMPLETE) {
      return;
    }

    const cache = getAutoCompleteErrorsCache();

    if (cache[rowId]?.[id] !== value) {
      setAutoCompleteErrorsCache(rowId, id, undefined);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, instanceId]);

  if (targetParentField(parentIndex, index)) {
    TEMP_CONTROLS[`${instanceId}+${gridIndex}`] = { controlHidden };
  }

  useEffect(() => {
    let isHidden = controlHidden;
    if (targetParentField(parentIndex, index)) {
      // find if any of the instances are visible
      Object.keys(TEMP_CONTROLS).forEach((key) => {
        if (key.split('#')[0] === id && !TEMP_CONTROLS[key].controlHidden) {
          isHidden = false;
        }
      });
    }

    bindControl(instanceId, {
      value,
      isViewOnly,
      isHidden,
      isRequired,
      label,
      containersPropsJson,
      rowId,
    });
  }, [
    instanceId,
    rowId,
    value,
    isViewOnly,
    controlHidden,
    isRequired,
    label,
    containersPropsJson,
    parentIndex,
    index,
    id,
  ]);

  useEffect(() => () => releaseControl(instanceId), [instanceId]);

  return false;
};

export default React.memo(VirtualDynamicInput);
